/**
 * The homepage: Continue Watching plus the scored rows.
 *
 * One request. The server builds all five rows concurrently and drops the empty ones,
 * so this component never has to decide whether a rail is worth rendering — an empty
 * rail on a screen reads as a bug, and the only place with enough information to know
 * a row is empty is the place that built it.
 *
 * Rails scroll horizontally rather than wrapping. That is not decoration: it is the
 * layout the TV remote navigates, and building the web version the same way means the
 * focus model has something coherent to attach to.
 */
import { Link } from 'react-router-dom';
import type { HomeRecommendationsDto, ProfileRecommendationDto } from '@filmodasi/shared';
import { useApi } from '../api/hooks';
import { ErrorBox, Empty, ItemCard, SkeletonRails } from '../components/ui';
import { ContinueCard, Rail } from '../components/rows';
import { FromHouseholdRail } from '../components/FromHouseholdRail';
import { Hero, type HeroProps } from '../components/Hero';

/**
 * What the billboard leads with.
 *
 * Continue Watching first, because "the thing I stopped halfway through" is the most
 * common reason anyone opens this app, and the row is already in hand. Otherwise the first
 * item of the first row — the server orders rows by how strongly it recommends them, so
 * that is its top pick, and reaching past it to invent a different rule would be inventing
 * a curator this household does not have.
 *
 * Returns null when there is nothing at all; Home renders its empty state instead.
 */
export function heroFor(data: HomeRecommendationsDto | null): HeroProps | null {
  const resume = data?.continueWatching[0];
  if (resume) {
    return {
      item: resume.item,
      series: resume.series,
      percent: resume.progress.percent,
      nextEpisode: resume.nextEpisode,
    };
  }
  const first = data?.rows.find((r) => r.items.length > 0)?.items[0];
  return first ? { item: first } : null;
}

export function HomePage() {
  const home = useApi<HomeRecommendationsDto>('/api/recommendations/home');
  /*
   * A second request, deliberately.
   *
   * These rows come from people, not from the recommender, and folding them into
   * `/api/recommendations/home` would put a message somebody sent behind the same six-hour
   * cache as a scored guess — so a recommendation could take until the evening to appear.
   * It is also the cheaper failure: if this call fails the homepage still renders, which
   * would not be true of one combined response.
   */
  const fromHousehold = useApi<ProfileRecommendationDto[]>('/api/profile-recommendations');

  /*
   * The page's own shape while it loads, not a spinner in the middle of a black screen.
   *
   * A spinner says "something is happening somewhere"; this says "rails are arriving here",
   * and — because the placeholders are the same width as the cards that replace them —
   * nothing jumps when they do. That reflow is most of what makes the homepage feel slow on
   * a merely ordinary connection.
   */
  if (home.loading && !home.data) {
    return (
      <main className="home">
        <p className="sr-only" role="status">
          Öneriler hazırlanıyor…
        </p>
        <SkeletonRails />
      </main>
    );
  }
  if (home.error) return <ErrorBox message={home.error} onRetry={home.reload} />;

  const data = home.data;
  const inbox = fromHousehold.data ?? [];
  const nothing =
    !data ||
    (data.continueWatching.length === 0 && data.rows.length === 0 && inbox.length === 0);

  if (nothing) {
    return (
      <main className="home">
        {/* The first screen a new household ever sees, so it names the next step rather
            than only reporting the absence. */}
        <Empty
          title="Burası henüz boş"
          action={<Link to="/settings">Ayarlara git</Link>}
        >
          Yayın aboneliğinizi ekleyip eşitleyin. Sonra birkaç şey izleyin — öneriler
          kendiliğinden dolmaya başlar.
        </Empty>
      </main>
    );
  }

  const hero = heroFor(data);

  return (
    <main className="home">
      {/*
        The billboard.

        Deliberately above the household inbox, and that is the one ordering choice here
        worth arguing about. A recommendation from a person outranks a score — which is why
        the inbox sits above every algorithmic row below — but the hero is not another row:
        it is the resume button for whatever this profile was already watching, and putting
        somebody else's suggestion in that slot would make the most common action on the
        screen the one that is hardest to reach.
      */}
      {hero && <Hero {...hero} />}

      {/* Above the algorithm's rows: somebody in the house went to the trouble of
          pointing at this, which outranks anything a score produced. */}
      <FromHouseholdRail items={inbox} onChanged={fromHousehold.reload} />

      {data.continueWatching.length > 0 && (
        <Rail title="İzlemeye Devam Et">
          {data.continueWatching.map((entry) => (
            <ContinueCard key={entry.item.id} entry={entry} />
          ))}
        </Rail>
      )}

      {data.rows.map((row) => (
        // `seed` is only ever set on a `because` row; every other row passes null and
        // renders exactly as before.
        <Rail title={row.title} seed={row.seed} key={row.key}>
          {row.items.map((item) => (
            <ItemCard key={item.id} item={item} />
          ))}
        </Rail>
      ))}
    </main>
  );
}
